import { titleize } from 'underscore.string';
import React from 'react';
import moment from 'moment';
import Link from 'components/v2/Link';
import { isString } from 'lodash';
import DatePicker from 'react-datepicker';
import CustomDropdown from 'components/v2/dropdowns/CustomDropdown';
import EditButton from '../../v2/buttons/EditButton';
import Badge from '../../v2/Badge';
import ExternalLinkIcon from '../../v2/icons/ExternalLinkIcon';

function getColor(status) {
  if (status === 'active') {
    return 'green';
  }
  return 'gray';
}

export const ITEMS_PER_PAGE_LIMIT = 10;

export const SEARCH_DELAY = 500;

export const breadcrumbs = {
  pages: [{ name: 'Yards', href: '/yards', current: true }],
};

export const setYardsListHeaders = (t) => {
  return [
    {
      field: 'name',
      label: t('YARD_HEADER_NAME'),
    },
    { field: 'address', label: t('YARD_HEADER_ADDRESS') },
    {
      field: 'marketCode',
      label: t('YARD_HEADER_MARKET'),
    },
    {
      field: 'vendor',
      label: t('YARD_HEADER_VENDOR'),
    },
    {
      field: 'numberOfSpaces',
      label: t('YARD_HEADER_NUMBER_OF_SPACES'),
    },
    {
      field: 'status',
      label: t('YARD_HEADER_STATUS'),
    },
    {
      field: 'secure',
      label: t('YARD_HEADER_SECURE'),
    },
    {
      field: 'actions',
      label: t('YARD_HEADER_ACTIONS'),
    },
  ];
};

export const createYardsListColumns = (history, t, vendors = {}) => {
  return [
    {
      field: 'nameInternal',
      label: t('YARD_HEADER_NAME'),
    },

    {
      field: 'address',
      label: t('YARD_HEADER_ADDRESS'),
    },
    {
      field: 'marketCode',
      label: t('YARD_HEADER_MARKET'),
    },
    {
      field: 'vendor',
      label: t('YARD_HEADER_VENDOR'),
      render: (vendorId) => {
        return (
          <div data-testid="venderIdToName">
            {vendors[vendorId] ? vendors[vendorId]?.account_name : vendorId}
          </div>
        );
      },
    },
    {
      field: 'spots',
      label: t('YARD_HEADER_NUMBER_OF_SPACES'),
    },
    {
      field: 'status',
      label: t('YARD_HEADER_STATUS'),
      render: (status) => {
        return <Badge color={getColor(status)}>{titleize(status)}</Badge>;
      },
    },
    {
      field: 'secure',
      label: t('YARD_HEADER_SECURE'),
      render: (secure) => {
        return (
          <Badge color={secure ? 'green' : 'gray'}>
            {secure ? 'Yes' : 'No'}
          </Badge>
        );
      },
    },
    {
      field: 'actions',
      label: t('YARD_HEADER_ACTIONS'),
      render: (_, row) => {
        return (
          <div data-testid="yardsListEditYardWrapper">
            <EditButton
              data-testid="yardsListEditYard"
              onClick={() => history.push(`/yards/${row.id}`)}
              color="blue"
            />
          </div>
        );
      },
    },
  ];
};

export const Actions = {
  SET_IS_LOADING: 'setIsLoading',
  SET_YARDS_RESPONSE: 'setYardsResponse',
  SET_ACTIVE_PAGE: 'setActivePage',
  SET_FETCH_PAYLOAD: 'setFetchPayload',
  SET_SEARCH_INPUT_VALUE: 'setSearchInputValue',
  SET_MARKETS_RESPONSE: 'setMarketsResponse',
};

const createBlkAddYardsActions = (t) => {
  return [
    {
      id: 'deliver_full_pickup_full',
      name: t('DELIVERY_FULL_PICKUP_FULL'),
    },
    {
      id: 'deliver_empty_pickup_empty',
      name: t('DELIVERY_EMPTY_PICKUP_EMPTY'),
    },
  ];
};

const datepickerStyles =
  'w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 text-left shadow-sm text-sm';

const setDateToDatePicker = (row, type) => {
  return row[type] ? moment(row[type]) : null;
};

export const createBulkAddYardsColumns = (
  t,
  handleContainerChange,
  handleYardChange,
  handleIntentChange,
  handleStopChange,
) => [
  {
    field: 'shipment_id',
    render: (shipmentId, row) => {
      return (
        <div data-testid="bulkAddYardsShipment">
          <Link url={`/shipments/${row.id}`}>
            <div className="flex align-middle items-center space-x-1">
              {shipmentId}
              <ExternalLinkIcon className="w-4 h-4" />
            </div>
          </Link>
        </div>
      );
    },
  },
  {
    field: 'equipments',
    render: (containerId, row) => {
      const prepared = row.equipmentsArr.map((container) => {
        return {
          id: container,
          equipment_number: container,
        };
      });

      return (
        <div data-testid="bulkAddYardsContainer">
          <CustomDropdown
            disabled={!prepared.length}
            onChange={handleContainerChange(row)}
            value={prepared.find((item) => item.id === containerId)}
            nameKey="equipment_number"
            options={prepared}
          />
        </div>
      );
    },
  },
  {
    field: 'yardId',
    render: (yardId, row) => {
      const prepared = row.yards.map((yard) => {
        return {
          id: yard.id,
          name: yard.nameInternal,
        };
      });
      return (
        <div data-testid="bulkAddYardsYard">
          <CustomDropdown
            onChange={handleYardChange(row)}
            value={prepared.find((yard) => yard.id === yardId)}
            options={prepared}
          />
        </div>
      );
    },
  },

  {
    field: 'intent',
    renderHeader: () => <div className="w-52">Yard Stop Intent</div>,
    render: (intentId, row) => {
      const bulkAddYardsActions = createBlkAddYardsActions(t);
      const intent = bulkAddYardsActions.find((item) => item.id === intentId);
      return (
        <div data-testid="bulkAddYardsIntent">
          <CustomDropdown
            onChange={handleIntentChange(row)}
            value={intent}
            options={bulkAddYardsActions}
          />
        </div>
      );
    },
  },
  {
    field: 'stop1_start',
    render: (_, row) => {
      const { stop1_end } = row;

      return (
        <DatePicker
          data-testid="bulkAddYardsStop1Start"
          selected={setDateToDatePicker(row, 'stop1_start')}
          className={datepickerStyles}
          placeholderText={t('BULK_ADD_YARD_START_DATE')}
          dropdownMode="select"
          onChange={handleStopChange({ type: 'stop1_start', row })}
          maxDate={stop1_end && isString(stop1_end) ? moment(stop1_end) : null}
        />
      );
    },
  },
  {
    field: 'stop1_end',
    render: (_, row) => {
      const { stop1_start } = row;

      return (
        <DatePicker
          data-testid="bulkAddYardsStop1End"
          selected={setDateToDatePicker(row, 'stop1_end')}
          dropdownMode="select"
          className={datepickerStyles}
          placeholderText={t('BULK_ADD_YARD_END_DATE')}
          onChange={handleStopChange({ type: 'stop1_end', row })}
          minDate={
            stop1_start && isString(stop1_start) ? moment(stop1_start) : null
          }
        />
      );
    },
  },
  {
    field: 'stop2_start',
    render: (_, row) => {
      const { stop2_end } = row;

      return (
        <DatePicker
          data-testid="bulkAddYardsStop2Start"
          selected={setDateToDatePicker(row, 'stop2_start')}
          dropdownMode="select"
          className={datepickerStyles}
          placeholderText={t('BULK_ADD_YARD_START_DATE')}
          onChange={handleStopChange({ type: 'stop2_start', row })}
          maxDate={stop2_end && isString(stop2_end) ? moment(stop2_end) : null}
        />
      );
    },
  },
  {
    field: 'stop2_end',
    render: (_, row) => {
      const { stop2_start } = row;

      return (
        <DatePicker
          data-testid="bulkAddYardsStop2End"
          selected={setDateToDatePicker(row, 'stop2_end')}
          dropdownMode="select"
          className={datepickerStyles}
          placeholderText={t('BULK_ADD_YARD_END_DATE')}
          onChange={handleStopChange({ type: 'stop2_end', row })}
          minDate={
            stop2_start && isString(stop2_start) ? moment(stop2_start) : null
          }
        />
      );
    },
  },
];

export const createBulkAddYardsHeaders = (t) => [
  {
    field: 'shipment_id',
    label: t('BULK_ADD_YARD_SHIPMENT'),
  },
  {
    field: 'container',
    label: t('BULK_ADD_YARD_CONTAINER'),
  },
  {
    field: 'yard',
    label: t('BULK_ADD_YARD_YARD'),
  },
  {
    field: 'intent',
    label: t('BULK_ADD_YARD_STOP_INTENT'),
  },
  {
    field: 'stop1_start',
    label: t('BULK_ADD_YARD_1_STOP_START'),
  },
  {
    field: 'stop1_end',
    label: t('BULK_ADD_YARD_1_STOP_END'),
  },
  {
    field: 'stop2_start',
    label: t('BULK_ADD_YARD_2_STOP_START'),
  },
  {
    field: 'stop2_end',
    label: t('BULK_ADD_YARD_2_STOP_END'),
  },
];

export const bulkYardsColumnWidths = [13, 13, 13, 20, 10, 10, 10, 10];
